import { Badge, breakpoints, Card, Inline, Stack, vars } from '@etg/wings';
import { css, mediaQueries } from '@eti/styles';
import type { MouseEvent, Dispatch, SetStateAction } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useDirection, useProperty, useSearch, useTranslation } from '@eti/providers';
import { TripCharacteristics } from '@eti/schema-types';
import { useMediaQuery } from 'usehooks-ts';
import { useHistory, useLocation } from 'react-router-dom';
import { addLegendNumberToSegmentEvents } from '../../../../../widgets/trip-details/utils/format';
import ItineraryDrawer from '../drawer/ItineraryDrawer';
import { useResultPageContext } from '../contexts/ResultPageContextProvider';
import { preSelectedExtraProductsData } from '../preSelectedProducts/preSelectedExtraProductsUtils';
import PreSelectedProducts from '../preSelectedProducts/PreSelectedProducts';
import { shouldShowDebugProviderSystemBar } from '../../../../../utils/debug';
import DebugProviderSystemBar from '../../../../../widgets/trip-details/components/DebugProviderSystemBar';
import { getTravelerPrices } from '../../../../../utils/travelers';
import { useGetTickets } from '../drawer/hooks/useGetTickets';
import usePreSelectedExtraProducts from '../preSelectedProducts/usePreSelectedExtraProducts';
import type { BoundsType, TripCampaignType, TripType } from '../../../models';
import { getSavedTrips } from '../../../../../common/custom-hooks/save-trips/utils';
import { isTripWithDifferentOriginOrDestination } from '../../../../../widgets/trip-details/utils/originDestinationUtil';
import { routes } from '../../../../../constants/routesAndApisConstants';
import { tripHasSelfTransfer } from '../utils/trip';
import { TripBaggageAndPriceInfo } from './components/TripBaggageAndPriceInfo';
import { TripTags, tripTagsWrapper, TripTagTitle } from './components/TripTags';
import type { RESULT_TRIP_TYPE } from './constants';
import { RESULT_TRIPS } from './constants';
import { TripCampaign } from './components/TripCampaign';
import TripDetailsBounds from './components/TripDetailsBounds';
import SelfTransferTagButton from './components/SelfTransferTagButton';

const tripDetailsBoundAndTagsWrapper = (isRTL: boolean) => css`
  flex: 1;
  gap: 0;
  justify-content: center;
  position: relative;

  ::after {
    border-right: 1px solid ${vars.colors.divider};
    content: '';
    height: calc(100% + 16px);
    left: ${isRTL ? 0 : ''};
    position: absolute;
    right: ${isRTL ? '' : 0};
    top: -8px;
    width: 1px;

    @media ${mediaQueries.small.only} {
      border-right: none;
    }
  }
`;

const resultCardStyles = css`
  padding: 8px 0;
`;

const preSelectedExtraProductsWrapperStyles = css`
  border-top: 1px solid ${vars.colors.divider};
  margin-top: 8px;
  padding: 16px 16px 8px;
`;

const debugPlaceholderStyles = css`
  margin-top: -25px;
`;

const badgeContainerStyles = (shouldShowDashedBorder: boolean) => css`
  ${shouldShowDashedBorder && `border-bottom: 1px dashed ${vars.colors.divider}`};
  padding: 8px 16px;
`;

interface ResultTripCardProps {
  trip: NonNullable<TripType>;
  isSponsoredTrip?: boolean;
  isSelectedTrip?: boolean;
  isRebookingTrip?: boolean;
  resultTripType?: RESULT_TRIP_TYPE;
  tripCampaign?: TripCampaignType;
  customStyles?: string;
  setIsSelfTransferModalOpen: Dispatch<SetStateAction<boolean>>;
}

const ResultTripCard = ({
  trip,
  isSponsoredTrip = false,
  isSelectedTrip = false,
  isRebookingTrip = false,
  resultTripType,
  tripCampaign,
  customStyles,
  setIsSelfTransferModalOpen,
}: ResultTripCardProps) => {
  const { p } = useProperty();
  const { t } = useTranslation();
  const { isRTL } = useDirection();
  const location = useLocation();
  const history = useHistory();
  const { currentSearchData } = useSearch();

  const {
    availableExtraProducts,
    tripCharacteristics = [],
    tripTags,
    bounds,
    id: flightId,
    isVI,
    tripId = '',
    type,
    shareableUrl,
    selectionKey,
    systems,
    travelerPrices,
    voucherAmount,
    tripTravelers,
    includedCabinBaggage,
    includedCheckedBaggage,
  } = trip;
  const shouldShowOneWayCombinationInfo = tripCharacteristics?.includes(
    TripCharacteristics.OneWayCombination,
  );
  const testId = resultTripType
    ? `result-${resultTripType.toLowerCase()}-trip-card`
    : 'result-trip-card';
  const boundsWithLegends: BoundsType = addLegendNumberToSegmentEvents(bounds as BoundsType);
  const isDifferentAirportNotificationEnabled = p('Result.DifferentAirportNotification.Enabled');
  const isChangeTripPage = window.location.pathname.includes(routes.CHANGE_TRIP);

  const tripHasDifferentOriginOrDestination = isTripWithDifferentOriginOrDestination(
    currentSearchData?.routes ?? [],
    boundsWithLegends,
  );
  const shouldShowDifferentAirportBadge =
    tripHasDifferentOriginOrDestination && isDifferentAirportNotificationEnabled;

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [shouldLoadOverlay, setShouldLoadOverlay] = useState(false);
  const shouldShowItineraryDrawer = p('Result.Page.V3.ItineraryDrawer.Enabled');
  const isSaveTripEnabled = p('Result.SaveTrip.Enabled');
  const isVISelfTransferDealEnabled = p('IbeClient.VI.SelfTransferDeal.Enabled');
  const isSelfTransferInTrip = tripHasSelfTransfer(bounds) && isVISelfTransferDealEnabled;

  const { setSelectedFlightId, isBaggagePopupActive, setIsBaggagePopupActive } =
    useResultPageContext();
  const isMobile = useMediaQuery(`(max-width: ${breakpoints._768})`);

  const preSelectedExtraProducts = usePreSelectedExtraProducts(
    availableExtraProducts,
    travelerPrices?.length,
  );
  const { totalPricePreSelectedProducts } = preSelectedExtraProductsData(preSelectedExtraProducts);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const urlSelectionKey = queryParams.get('selectionKey');
    const savedTripSelectedKeys = getSavedTrips().map((saveTrip) => saveTrip.selectionKey);
    const doSavedAndUrlSelectionKeyMatch = savedTripSelectedKeys.includes(urlSelectionKey ?? '');
    if (
      shouldShowItineraryDrawer &&
      isSaveTripEnabled &&
      doSavedAndUrlSelectionKeyMatch &&
      selectionKey === urlSelectionKey
    ) {
      setSelectedFlightId(flightId);
      setIsDrawerOpen(true);
      queryParams.delete('selectionKey');
      history.replace({
        pathname: location.pathname,
        search: queryParams.toString(),
      });
    }
  }, [
    flightId,
    history,
    location.pathname,
    location.search,
    selectionKey,
    setSelectedFlightId,
    shouldShowItineraryDrawer,
    isSaveTripEnabled,
  ]);

  useEffect(() => {
    let delay: any;
    if (!isDrawerOpen) {
      delay = setTimeout(() => {
        setShouldLoadOverlay(false);
      }, 510);
    } else {
      setShouldLoadOverlay(isDrawerOpen);
    }
    return () => {
      clearTimeout(delay);
    };
  }, [isDrawerOpen]);

  const toggleOverlay = useCallback(
    (e: MouseEvent<HTMLElement>) => {
      if (!isBaggagePopupActive) {
        e.stopPropagation();
        setSelectedFlightId(flightId!);
        setIsDrawerOpen((prevDrawerState) => !prevDrawerState);
      } else {
        setIsBaggagePopupActive(false);
      }
    },
    [isBaggagePopupActive, flightId, setSelectedFlightId, setIsBaggagePopupActive],
  );

  const travelerPriceBreakdown = getTravelerPrices(tripTravelers, travelerPrices);
  const discountAmount = voucherAmount?.value;
  const tickets = useGetTickets(
    availableExtraProducts,
    bounds,
    preSelectedExtraProducts,
    travelerPriceBreakdown,
    discountAmount,
  );
  const isDebugEnabled = window.spa_variables?.debugEnabled;
  const shouldCardBeHalfClickable = isMobile && isVISelfTransferDealEnabled;

  return (
    <>
      <Card
        className={customStyles}
        data-shareable-url={shareableUrl}
        data-testid={testId}
        onClick={isMobile && !isVISelfTransferDealEnabled ? toggleOverlay : undefined}
        padding={0}
        role={isMobile && !isVISelfTransferDealEnabled ? 'button' : undefined}
      >
        {shouldShowDebugProviderSystemBar(systems) && (
          <DebugProviderSystemBar providerSystems={systems} />
        )}
        <Inline className={resultCardStyles} collapseBelow={breakpoints._768} spacing={0}>
          <Stack className={tripDetailsBoundAndTagsWrapper(isRTL)} spacing={8}>
            <Inline
              className={badgeContainerStyles(shouldCardBeHalfClickable)}
              data-testid="trip-tags"
              spacing={8}
            >
              {!isRebookingTrip && resultTripType && (
                <>
                  {resultTripType === RESULT_TRIPS.SPONSORED && (
                    <TripTags
                      isOneWay={type === 'ONE_WAY'}
                      tripCharacteristics={tripCharacteristics}
                      tripTags={tripTags}
                    />
                  )}
                  {resultTripType === RESULT_TRIPS.RECOMMENDED && (
                    <Inline className={tripTagsWrapper}>
                      <TripTagTitle>{t('Result.TripTag.Recommended.Label')}</TripTagTitle>
                    </Inline>
                  )}
                </>
              )}
              {isSelfTransferInTrip && (
                <SelfTransferTagButton setIsSelfTransferModalOpen={setIsSelfTransferModalOpen} />
              )}
              {shouldShowDifferentAirportBadge && !isChangeTripPage && (
                <Inline aria-label={t('Result.NearbyAirports.Badge.Text')}>
                  <Badge aria-hidden data-testid="nearby-airports-badge" variant="caution">
                    {t('Result.NearbyAirports.Badge.Text')}
                  </Badge>
                </Inline>
              )}
            </Inline>

            <TripDetailsBounds
              boundsWithLegends={boundsWithLegends}
              flightId={flightId}
              onClick={shouldCardBeHalfClickable ? toggleOverlay : undefined}
              shouldShowOneWayCombinationInfo={shouldShowOneWayCombinationInfo}
              type={type}
            />
            {preSelectedExtraProducts?.length > 0 && (
              <div className={preSelectedExtraProductsWrapperStyles}>
                <PreSelectedProducts preSelectedExtraProducts={preSelectedExtraProducts} />
              </div>
            )}
          </Stack>
          <TripBaggageAndPriceInfo
            availableExtraProducts={availableExtraProducts}
            bounds={bounds}
            includedCabinBaggage={includedCabinBaggage}
            includedCheckedBaggage={includedCheckedBaggage}
            isVI={isVI}
            numberOfTravelers={tripTravelers?.length}
            onButtonClick={toggleOverlay}
            onClick={shouldCardBeHalfClickable ? toggleOverlay : undefined}
            ticket={tickets[0]}
          />
        </Inline>
        {tripCampaign && <TripCampaign tripCampaign={tripCampaign} />}
      </Card>
      {tripId && isDebugEnabled && (
        <div
          className={debugPlaceholderStyles}
          data-testid="debug-trip-placeholder"
          data-tripid={tripId}
        />
      )}
      {shouldLoadOverlay && shouldShowItineraryDrawer && (
        <ItineraryDrawer
          bounds={bounds}
          isOpen={isDrawerOpen}
          isSelectedTrip={isSelectedTrip}
          isSponsoredTrip={isSponsoredTrip}
          isVI={isVI}
          onClose={toggleOverlay}
          preSelectedExtraProducts={preSelectedExtraProducts}
          shouldShowOneWayCombinationInfo={shouldShowOneWayCombinationInfo}
          totalPricePreSelectedProducts={totalPricePreSelectedProducts}
        />
      )}
    </>
  );
};

export default ResultTripCard;
